import { FunctionComponent, useMemo, useState, useEffect } from 'react';
import styles from './MonthlyNetSales.module.scss';
import { MonthlyNetSalesTable, MonthlyNetSalesRow } from './MonthlyNetSalesTable';
import MonthlyNetSalesLineChart from './MonthlyNetSalesLineChart';
import Header from '../../components/Header';
import { Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone, useMarketplace } from 'hooks';
import ToggleTabs from 'components/Insights/components/ToogleTabs/ToggleTabs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { getMonthlyNetSalesReport } from 'actions/insights/sales/monthlyNetSalesReport/MonthlyNetSalesReportActions';
import type { MonthlyNetSalesReport } from 'model/MonthlyNetSalesReport';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';

interface Props {
    description?: string;
}

const options = [
    { id: 'table', label: 'Table' },
    { id: 'chart', label: 'Chart' }
];

export const MonthlyNetSales: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();
    const { report, loading } = useSelector((s: RootState) => s.monthlyNetSalesReport);
    const { thisYearOption, lastYearOption, nextYearOption } = useDateRangeOptions();

    const defaultPeriod: Period = useMemo(() => {
        const now = moment().tz(timeZone);
        return {
            from: now.clone().startOf('year'),
            to: now.clone().endOf('year')
        };
    }, [timeZone]);

    const [period, setPeriod] = useState<Period | null>(defaultPeriod);
    const [selectedYear, setSelectedYear] = useState<number>(moment().tz(timeZone).year());
    const [currentTab, setCurrentTab] = useState('table');

    const dateRangeOptions = [thisYearOption, lastYearOption, nextYearOption];

    useEffect(() => {
        dispatch(getMonthlyNetSalesReport(selectedYear));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear]);

    const buildRows = (r: MonthlyNetSalesReport): MonthlyNetSalesRow[] => {
        const byMonth = (months: MonthlyNetSalesReport['rows'][number]['months']) => {
            const map = new Map(months.map(m => [m.month, m.netSales]));
            return Array.from({ length: 12 }, (_, i) => Number(map.get(i + 1) ?? 0));
        };

        return (r?.rows ?? []).map(row => ({
            category: row.label,
            monthly: byMonth(row.months),
            total: row.yearTotal,
            percentage: row.pct
        }));
    };

    const rows = useMemo(() => buildRows(report), [report]);

    const formatCurrency = (v: number, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 3
        }).format(v);
    };

    const headers = [
        'Category',
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
        String(selectedYear),
        'Percentage'
    ];

    const csvData = rows.map(r => [
        r.category,
        ...r.monthly.map(v => formatCurrency(v)),
        formatCurrency(r.total),
        (Number(r.percentage ?? 0) * 100).toFixed(3)
    ]) as string[][];

    const exportFile = {
        name: `Monthly Net Sales - ${marketplace.basics.businessName}.csv`,
        dataInMemory: true,
        dataToExport: csvData,
        headers
    };

    const calendar = { optionSelected: String(selectedYear) };

    const handleChangePeriod = (p: Period | null, allTime: boolean) => {
        if (allTime || !p) {
            const year = moment().tz(timeZone).year();
            setSelectedYear(year);
            setPeriod(defaultPeriod);
            dispatch(getMonthlyNetSalesReport(year));
            return;
        }

        const year = p.from.clone().tz(timeZone).year();

        setSelectedYear(year);
        setPeriod({
            from: p.from.clone().startOf('year'),
            to: p.from.clone().endOf('year')
        });

        dispatch(getMonthlyNetSalesReport(year));
    };

    return (
        <div className={styles.container}>
            {props.description && <span className={styles.description}>{props.description}</span>}

            <div className={styles.categoryContainer}>
                <ToggleTabs
                    options={options}
                    value={currentTab}
                    setValue={setCurrentTab}
                />
            </div>

            <div className={styles.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                    dateOptions={dateRangeOptions}
                />
            </div>

            {currentTab === 'table' && (
                <div className={styles.tableContainer}>
                    <MonthlyNetSalesTable
                        rows={rows}
                        year={selectedYear}
                        loading={loading}
                    />
                </div>
            )}

            {currentTab === 'chart' && (
                <div className={styles.chartContainer}>
                    <MonthlyNetSalesLineChart
                        report={report}
                        loading={loading}
                    />
                </div>
            )}
        </div>
    );
};

export default MonthlyNetSales;
